import { LightningElement, api, track } from "lwc";

/** Each letter of the alphabet for the lettering of choices */
export const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/** Allow IDs to be strings or numbers */
type ID = string | number;

/** Represents one of the choices for a question */
interface Choice {
  id: ID;
  label: string;
}

/** Represents a question in a quiz */
export interface Question {
  id: ID;
  label: string;
  choices: Choice[];
}

/** Presentational (API-agnostic) multiple choice quiz component */
export default class Quiz extends LightningElement {
  /** The main text for the quiz */
  @api title = "";

  /** Optional secondary text in the header */
  @api subtitle = "";

  /** Optional label indicating number of points to be earned (i.e. "+100 points") */
  @api pointsLabel = "";

  /** Label for the submit button */
  @api submitLabel = "";

  /** The text in the footer when you haven't submitted */
  @api initialText = "";

  /** The text in the footer the the quiz is loading */
  @api loadingText = "";

  /** The text in the footer when some selected answers are incorrect */
  @api errorText = "";

  /** The text in the footer when all selected answers are correct */
  @api successText = "";

  /** The quiz is loading (i.e. during submission) */
  @api loading = false;

  /** The questions for the quiz */
  @api questions: Question[] = [];

  /**
   * indeces represent the index of the question and values represent if the last
   * submission was correct. if results is empty, the quiz has not been submitted.
   */
  @api results: boolean[] = [];

  /**
   * indeces represent the index of the question and values the index of the
   * currently selected answer. falsy values mean the question hasn't been answered
   */
  @track selectedAnswers: number[] = [];

  /** The selected answers for the most recent submission */
  @track lastSubmission: number[] = [];

  /** Holds the question API data and UI state for each question */
  get questionElements() {
    return this.questions.map((question, questionIndex) => ({
      data: question,
      number: questionIndex + 1,
      labelClass: this.questionLabelClass(questionIndex),
      choices: question.choices.map((choice, choiceIndex) => ({
        data: choice,
        letter: alphabet[choiceIndex],
        labelClass: this.choiceLabelClass(questionIndex, choiceIndex),
        disabled: this.isChoiceDisabled(questionIndex),
      })),
    }));
  }

  /** Has an answer been selected for each question? */
  get allQuestionsAnswered() {
    return this.questions.every(
      (_, index) => typeof this.selectedAnswers[index] === "number"
    );
  }

  /** Is the submit button disabled? */
  get submitDisabled() {
    return !this.allQuestionsAnswered || this.loading || this.allAnswersCorrect;
  }

  /** The text to display in the footer */
  get footerText() {
    if (this.loading) {
      return this.loadingText;
    } else if (this.isAnyChoiceIncorrect) {
      return this.errorText;
    } else if (this.allAnswersCorrect) {
      return this.successText;
    } else {
      return this.initialText;
    }
  }

  /** What classes to apply to the footer text (i.e. red, green) */
  get footerTextClass() {
    const classes = ["footer-text"];

    if (this.isAnyChoiceIncorrect) {
      classes.push("footer-text_incorrect");
    } else if (this.allAnswersCorrect) {
      classes.push("footer-text_success");
    }

    return classes.join(" ");
  }

  /** Did the user finish loading and get results? */
  get didGetResults() {
    return !this.loading && this.results.length === this.questions.length;
  }

  /** Are all answers correct? */
  get allAnswersCorrect() {
    return this.didGetResults && this.results.every(Boolean);
  }

  /** Is an incorrect choice for any question selected? */
  get isAnyChoiceIncorrect() {
    return this.questions.some((_, questionIndex) =>
      this.isIncorrectChoiceSelected(questionIndex)
    );
  }

  /** Classes for the question labels depending on state */
  questionLabelClass(questionIndex: number) {
    const classes = ["question-label"];

    if (this.isIncorrectChoiceSelected(questionIndex)) {
      classes.push("question-label_incorrect");
    }

    return classes.join(" ");
  }

  /** Classes for the choice labels depending on state */
  choiceLabelClass(questionIndex: number, choiceIndex: number) {
    const classes = ["choice-label"];

    if (this.isChoiceIncorrect(questionIndex, choiceIndex)) {
      classes.push("choice-label_incorrect");
    } else if (this.isChoiceCorrect(questionIndex, choiceIndex)) {
      classes.push("choice-label_correct");
    }

    return classes.join(" ");
  }

  /** Was the last submission for this question incorrect? */
  isQuestionIncorrect(questionIndex: number) {
    return this.didGetResults && !this.results[questionIndex];
  }

  /** Is the given choice for the question incorrect? */
  isChoiceIncorrect(questionIndex: number, choiceIndex: number) {
    return (
      this.isQuestionIncorrect(questionIndex) &&
      this.lastSubmission[questionIndex] === choiceIndex
    );
  }

  /** Is an incorrect choice selected? */
  isIncorrectChoiceSelected(questionIndex: number) {
    return (
      this.isQuestionIncorrect(questionIndex) &&
      this.selectedAnswers[questionIndex] === this.lastSubmission[questionIndex]
    );
  }

  /** Is a choice disabled? */
  isChoiceDisabled(questionIndex: number) {
    return this.loading || this.isQuestionCorrect(questionIndex);
  }

  /** Is the choice for a question correct? */
  isChoiceCorrect(questionIndex: number, choiceIndex: number) {
    return (
      this.isQuestionCorrect(questionIndex) &&
      this.lastSubmission[questionIndex] === choiceIndex
    );
  }

  /** Is the question correct? */
  isQuestionCorrect(questionIndex: number) {
    return this.didGetResults && this.results[questionIndex];
  }

  /** When a new choice is selected for a question */
  handleInputChange(event: Event) {
    const target = event.target as HTMLInputElement;

    const choiceIndex = parseInt(target.value, 10);
    const questionIndex = parseInt(target.dataset.questionIndex!, 10);

    this.selectedAnswers[questionIndex] = choiceIndex;

    this.dispatchEvent(
      new CustomEvent("select", {
        detail: { selectedAnswers: this.selectedAnswers },
      })
    );
  }

  /** When the user clicks submit */
  handleSubmit() {
    this.lastSubmission = this.selectedAnswers.slice();
    this.dispatchEvent(
      new CustomEvent("submit", {
        detail: { selectedAnswers: this.selectedAnswers },
      })
    );
  }
}
